/**
 * Theme descriptors keyed by MapId (MapDef.audioTheme). Pitches are MIDI note numbers; scales
 * are semitone offsets from the tonic. Both themes are in D so crossfades never clash.
 */
import type { MapId } from '@cloudpearl/config';
import type { ReverbSpec } from './noise';
import { mod } from './util';

export interface ThemeDef {
  readonly id: MapId;
  readonly scale: readonly number[];
  /** Landing-chime degree 0 (combo 1). */
  readonly chimeRoot: number;
  /** UI tick register. */
  readonly uiRoot: number;
  /** Melody degree 0. */
  readonly melodyRoot: number;
  readonly reverb: ReverbSpec;
  /** Reverb return level. */
  readonly reverbLevel: number;
  /** Music tempo range while playing (calm → full flow). */
  readonly bpm: readonly [number, number];
  readonly idleBpm: number;
  /** Crash "aww" motif (3 descending notes). */
  readonly sadMotif: readonly number[];
  /** New-best ascending run. */
  readonly fanfare: readonly number[];
}

/** D major pentatonic — bright, airy, glassy. */
export const SKY: ThemeDef = {
  id: 'sky',
  scale: [0, 2, 4, 7, 9],
  chimeRoot: 74, // D5
  uiRoot: 86, // D6
  melodyRoot: 62, // D4
  reverb: { seconds: 2.2, preDelay: 0.022, dampStart: 9500, dampEnd: 2400, bloom: 0.03 },
  reverbLevel: 1,
  bpm: [86, 118],
  idleBpm: 84,
  sadMotif: [78, 76, 71], // F#5 E5 B4 — lands on the relative minor, a soft "aww"
  fanfare: [74, 78, 81, 86, 90, 93, 98], // D F# A D F# A D
};

/** D Hijaz (phrygian dominant): D Eb F# G A Bb C — warm, mysterious, hot. */
export const DUNES: ThemeDef = {
  id: 'dunes',
  scale: [0, 1, 4, 5, 7, 8, 10],
  chimeRoot: 74, // D5
  uiRoot: 74, // D5 (wood)
  melodyRoot: 62, // D4
  reverb: { seconds: 1.4, preDelay: 0.012, dampStart: 6000, dampEnd: 1300, bloom: 0.012 },
  reverbLevel: 0.8,
  bpm: [82, 112],
  idleBpm: 80,
  sadMotif: [67, 66, 63], // G F# Eb — the augmented-second sigh
  fanfare: [62, 63, 66, 67, 69, 70, 72, 74], // full Hijaz run D→D
};

export const THEMES: Readonly<Record<MapId, ThemeDef>> = { sky: SKY, dunes: DUNES };

export function themeFor(id: MapId | string): ThemeDef {
  return id === 'dunes' ? DUNES : SKY;
}

/** Scale degree (any integer, wraps by octave) → MIDI. */
export function degreeMidi(theme: ThemeDef, degree: number, root: number): number {
  const len = theme.scale.length;
  const oct = Math.floor(degree / len);
  return root + oct * 12 + (theme.scale[mod(degree, len)] as number);
}
